import { faDownload } from '@fortawesome/free-solid-svg-icons';
import React, { useEffect, useState } from 'react';
import { Col, Row, Table } from 'react-bootstrap';
import { CSVLink } from 'react-csv';
import ReactPaginate from 'react-paginate';
import { fetchReportResources } from '../../../services/httpClient';
import IconEmbeddedButton from '../../ui/IconEmbeddedButton';

interface ResourcesAssignedTableState {
  data?: any[];
  startDate?: string;
  endDate?: string;
  useDatePicker?: boolean;
  resourceType?: string;
}

export const ResourcesAssignedTable = ({ data, startDate, endDate, useDatePicker = false, resourceType }: ResourcesAssignedTableState) => {
  const [resourceData, setResourceData] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;
  const pageCount = Math.ceil(resourceData.length / itemsPerPage);
  const offset = currentPage * itemsPerPage;
  const currentData = resourceData.slice(offset, offset + itemsPerPage);

  useEffect(() => {
    const fetchData = async () => {
      if (data) {
        setResourceData(data as any[]);
      } else {
        const starting = new Date();
        starting.setDate(starting.getDate() - 365);
        let start = starting.toISOString().slice(0, 10);
        let end = new Date().toISOString().slice(0, 10);
        if (useDatePicker) {
          start = startDate ?? start;
          end = endDate ?? end;
        }
        await getResourceData(start!, end!);
      }
    }
    fetchData();
  }, [data, startDate, endDate, useDatePicker]);

  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hour = String(now.getHours()).padStart(2, "0");
    const minute = String(now.getMinutes()).padStart(2, "0");
    const second = String(now.getSeconds()).padStart(2, "0");
    return `${year}${month}${day}${hour}${minute}${second}`;
  };

  const shapeData = (resources: any[]) => {
    const data = resources.filter((r) => r.resource_type_key === resourceType);

    const grouped = data.reduce((acc, item) => {
      const key = `${item.assigned_at}-${item.resource_id}-${item.assignee_id}`;

      if (!acc[key]) {
        acc[key] = {
          assigned_at: item.assigned_at,
          resource_id: item.resource_id,
          resource_name: item.resource_name,
          resource_category: item.resource_category,
          assignee_id: item.assignee_id,
          assignee_name: item.assignee_name,
          quantity: 0,
        };
      }

      acc[key].quantity += 1;

      return acc;
    }, {} as Record<string, any>);

    return Object.values(grouped).sort((a: any, b: any) => new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime());
  };
  
  const getResourceData = (start: string, end: string) => {
    fetchReportResources(start, end)
      .then((res: any) => {
        const resources = res[0].data;
        setResourceData(shapeData(resources));
      })
      .catch((err: any) => {
        console.log(err);
      })
  };

  const headers = [
    { label: "Resource", key: "resource_name" },
    { label: "Category", key: "resource_category" },
    { label: "Quantity", key: "quantity" },
    { label: "Person", key: "assignee_name" },
    { label: "Date", key: "assigned_at" },
  ];

  return (
    <>
      <Row>
        <Col>
          <h5 className="app-header-text">{`${resourceType === "consumable" ? "Consumables" : "Non-Consumables"} Assigned`}</h5>
        </Col>
        <Col className="text-end">
          {data && (
            <CSVLink
              id={`download-${resourceType}s-button`}
              data={resourceData}
              headers={headers}
              filename={`${resourceType}s${getCurrentDateTime()}.csv`}
              style={{ display: "inline-block" }}
              target="_blank"
            >
              <IconEmbeddedButton tooltipText="Data Export" icon={faDownload} />
            </CSVLink>
          )}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col className="col-12">
          <Table striped>
            <thead>
              <tr>
                <th>Resource</th>
                <th>Category</th>
                <th>Quantity</th>
                <th>Person</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {currentData?.map((d: any, index: number) => 
                <tr key={index}>
                  <td>{d.resource_name}</td>
                  <td>{d.resource_category ?? "None"}</td>
                  <td>{d.quantity}</td>
                  <td>{d.assignee_name}</td>
                  <td className="text-nowrap">{d.assigned_at}</td>
                </tr>
              )}
            </tbody>
          </Table>
          {pageCount > 1 && (
            <div id={`${resourceType}s-pagination`} style={{ position: 'relative', zIndex: 0 }}>
              <ReactPaginate
                previousLabel={"← Previous"}
                nextLabel={"Next →"}
                pageCount={pageCount}
                onPageChange={(event: { selected: number }) => setCurrentPage(event.selected)}
                containerClassName={"pagination justify-content-center"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextClassName={"page-item"}
                nextLinkClassName={"page-link"}
                activeClassName={"active"}
                forcePage={currentPage}
              />
            </div>
          )}
        </Col>
      </Row>
    </>
  );
};
